import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Plus, Trash2, Save, CheckCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { API } from "../App";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const EXERCISE_CONFIGS = {
  "ado_school_stress_journal": {
    titleKey: "ado.schoolStress.title",
    defaultTitle: "Mon journal de stress scolaire",
    descKey: "ado.schoolStress.desc",
    defaultDesc: "Chaque jour de classe, 2 minutes : ton niveau de stress, ce qui s'est passé, ce que tu t'es dit, ce que tu as fait et ce qui t'a aidé.",
    fields: [
      { id: "day", label: "Jour", type: "select", options: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"] },
      { id: "moment", label: "Moment", type: "select", options: ["Avant d'aller au collège / lycée", "Dans un cours", "À la récré / cantine", "Devoirs le soir", "Autre"] },
      { id: "stress", label: "Niveau de stress (0-10)", type: "number", min: 0, max: 10 },
      { id: "situation", label: "Ce qui s'est passé (en une phrase)", type: "text" },
      { id: "thought", label: "Ce que je me suis dit", type: "textarea" },
      { id: "action", label: "Ce que j'ai fait", type: "select", options: ["Je suis resté(e) et j'ai géré", "Je suis resté(e) mais c'était dur", "J'ai évité / je suis sorti(e)", "J'ai demandé de l'aide", "Autre"] },
      { id: "helped", label: "Ce qui m'a aidé (respiration, un pote, un adulte, une pensée…)", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 5,
  },
  "ado_exposure_plan": {
    titleKey: "ado.exposurePlan.title",
    defaultTitle: "Mon plan de défis en classe",
    defaultDesc: "Construis ton échelle de défis, du plus facile au plus difficile, et coche-les un par un. Note ton anxiété avant et après : elle baisse presque toujours plus vite que prévu.",
    descKey: "ado.exposurePlan.desc",
    fields: [
      { id: "step", label: "Numéro du défi (1 = le plus facile)", type: "number", min: 1, max: 10 },
      { id: "challenge", label: "Mon défi", type: "select", options: ["Rester en classe 10 minutes de plus", "Rester toute l'heure sans sortir", "Répondre à une question quand on m'interroge", "Poser une question au prof", "Lire à voix haute", "Passer au tableau", "Parler à un(e) camarade que je connais peu", "Manger à la cantine avec d'autres", "Présenter un exposé", "Autre (à décrire)"] },
      { id: "details", label: "Précisions (quel cours, avec qui, quand)", type: "text" },
      { id: "anxiety_before", label: "Anxiété prévue avant (0-10)", type: "number", min: 0, max: 10 },
      { id: "anxiety_after", label: "Anxiété réelle après (0-10)", type: "number", min: 0, max: 10 },
      { id: "done", label: "Fait ?", type: "select", options: ["Oui !", "En partie", "Pas encore — je réessaie"] },
      { id: "pride", label: "Ma fierté / ce que j'ai appris", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 8,
  },
  "grief_memory_journal": {
    titleKey: "grief.memoryJournal.title",
    defaultTitle: "Journal des souvenirs",
    descKey: "grief.memoryJournal.desc",
    defaultDesc: "Une exposition graduée aux souvenirs : choisissez un souvenir (photo, objet, lieu, moment partagé), commencez par les plus faciles, racontez-le en détail, puis notez ce que vous ressentez avant et après. Avec la répétition, les souvenirs redeviennent des souvenirs, tristes mais supportables.",
    fields: [
      { id: "trigger", label: "Le souvenir choisi (photo, objet, lieu, moment, date…)", type: "text" },
      { id: "difficulty", label: "Difficulté anticipée (0 = facile, 10 = très douloureux)", type: "number", min: 0, max: 10 },
      { id: "emotion_before", label: "Intensité émotionnelle avant (0-10)", type: "number", min: 0, max: 10 },
      { id: "narrative", label: "Le souvenir raconté en détail (où, quand, qui, ce qui s'est passé, ce que je ressentais alors)", type: "textarea" },
      { id: "meaning", label: "Ce que ce souvenir dit de notre lien, ou ce que la personne m'a transmis", type: "textarea" },
      { id: "emotion_after", label: "Intensité émotionnelle après (0-10)", type: "number", min: 0, max: 10 },
      { id: "emotions", label: "Émotions ressenties", type: "select", options: ["Tristesse", "Tendresse / chaleur", "Manque", "Colère", "Culpabilité", "Apaisement", "Gratitude", "Mélange de plusieurs"] },
      { id: "learning", label: "Ce que j'observe (la vague est-elle montée puis redescendue ? qu'est-ce qui m'a surpris(e) ?)", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 12,
  },
  "grief_letter": {
    titleKey: "grief.letter.title",
    defaultTitle: "Lettre à la personne disparue",
    descKey: "grief.letter.desc",
    defaultDesc: "Écrivez à la personne ce que vous n'avez pas pu lui dire : ce que vous regrettez, ce dont vous lui êtes reconnaissant(e), ce qui vous met en colère, ce qui se passe dans votre vie. Puis imaginez ce qu'elle vous répondrait. Personne n'est obligé de lire cette lettre.",
    fields: [
      { id: "recipient", label: "À qui j'écris (prénom, lien)", type: "text" },
      { id: "unsaid", label: "Ce que je n'ai pas pu te dire", type: "textarea" },
      { id: "gratitude", label: "Ce que je te dois, ce dont je te remercie", type: "textarea" },
      { id: "regrets_anger", label: "Ce que je regrette, ce qui me met en colère (envers toi, envers moi, envers d'autres)", type: "textarea" },
      { id: "news", label: "Ce qui se passe dans ma vie depuis ton départ", type: "textarea" },
      { id: "reply", label: "Ce que j'imagine que tu me répondrais", type: "textarea" },
      { id: "feeling_after", label: "Comment je me sens après avoir écrit", type: "select", options: ["Apaisé(e)", "Très ému(e) mais soulagé(e)", "Triste", "En colère", "Vidé(e)", "Mitigé(e)"] },
      { id: "next_step", label: "Ce que j'ai envie de faire de cette lettre (la garder, la relire en séance, la déposer quelque part, en écrire une autre…)", type: "text" },
    ],
    repeatable: true,
    maxEntries: 6,
  },
  "grief_avoidance_hierarchy": {
    titleKey: "grief.avoidance.title",
    defaultTitle: "Hiérarchie des situations évitées",
    descKey: "grief.avoidance.desc",
    defaultDesc: "Listez ce que vous évitez depuis le décès (lieux, objets, photos, personnes, conversations, activités), cotez la difficulté de 0 à 10, puis affrontez ces situations une par une, de la plus facile à la plus difficile, si possible accompagné(e). Chaque étape montre à votre cerveau que la douleur est supportable.",
    fields: [
      { id: "situation", label: "Situation évitée", type: "text" },
      { id: "category", label: "Type", type: "select", options: ["Lieu (chambre, cimetière, hôpital, restaurant…)", "Objet / affaires personnelles", "Photos, vidéos, messages", "Personne ou groupe", "Conversation / parler de la personne", "Activité partagée autrefois", "Date ou événement", "Autre"] },
      { id: "difficulty", label: "Difficulté (0-10)", type: "number", min: 0, max: 10 },
      { id: "feared", label: "Ce que je crains qu'il se passe si je m'y confronte", type: "textarea" },
      { id: "plan", label: "Mon plan : quand, comment, avec qui", type: "textarea" },
      { id: "done", label: "Fait ?", type: "select", options: ["Oui", "En partie", "Pas encore"] },
      { id: "peak", label: "Intensité maximale ressentie pendant (0-10)", type: "number", min: 0, max: 10 },
      { id: "outcome", label: "Ce qui s'est réellement passé et ce que j'en retiens", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 10,
  },
  "grief_reengagement_plan": {
    titleKey: "grief.reengagement.title",
    defaultTitle: "Plan de réengagement",
    descKey: "grief.reengagement.desc",
    defaultDesc: "Chaque semaine, choisissez une valeur ou un domaine de vie qui compte pour vous et planifiez une activité concrète : quand, où, avec qui. Anticipez les obstacles (fatigue, culpabilité, « à quoi bon »), puis faites le bilan. Se réengager n'est pas oublier : c'est continuer la vie que la personne aurait voulue pour vous.",
    fields: [
      { id: "week", label: "Semaine du", type: "text" },
      { id: "domain", label: "Valeur ou domaine de vie", type: "select", options: ["Relations / amis", "Famille", "Travail / études", "Santé / corps / sport", "Loisirs / créativité", "Nature / spiritualité", "Engagement / transmission", "Apprendre une tâche que la personne assurait", "Autre"] },
      { id: "activity", label: "Activité concrète (quoi, quand, où, avec qui)", type: "textarea" },
      { id: "why", label: "Pourquoi cela compte pour moi (et ce que la personne en aurait pensé)", type: "textarea" },
      { id: "obstacles", label: "Obstacles anticipés et comment je les contourne", type: "textarea" },
      { id: "done", label: "Réalisé ?", type: "select", options: ["Oui", "En partie", "Non, reporté"] },
      { id: "pleasure", label: "Plaisir ressenti (0-10)", type: "number", min: 0, max: 10 },
      { id: "meaning", label: "Sens / fierté ressentis (0-10)", type: "number", min: 0, max: 10 },
      { id: "review", label: "Bilan : ce que j'en retire, ce que je garde pour la semaine prochaine", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 12,
  },
  "perf_journal": {
    titleKey: "perf.journal.title",
    defaultTitle: "Journal du perfectionnisme",
    descKey: "perf.journal.desc",
    defaultDesc: "À chaque fois que vous sentez la pression de la perfection : notez la situation, le standard exigé, la pensée « je dois », ce que vous avez fait, ce que cela a coûté, puis reformulez le standard en version excellenciste (élevé mais atteignable).",
    fields: [
      { id: "situation", label: "Situation (quoi, où, avec qui)", type: "text" },
      { id: "domain", label: "Domaine", type: "select", options: ["Travail / études", "Relations / famille", "Apparence / corps", "Maison / organisation", "Loisirs / sport", "Thérapie", "Autre"] },
      { id: "standard", label: "Le standard que je m'impose (« ce doit être… »)", type: "textarea" },
      { id: "must_thought", label: "La pensée « je dois / il faut » exacte", type: "textarea" },
      { id: "emotion_intensity", label: "Tension / anxiété ressentie (0-10)", type: "number", min: 0, max: 10 },
      { id: "behavior", label: "Ce que j'ai fait", type: "select", options: ["Vérifié / relu plusieurs fois", "Sur-préparé", "Reporté / évité", "Refait entièrement", "Demandé confirmation", "Comparé aux autres", "Terminé et laissé tel quel", "Autre"] },
      { id: "cost", label: "Ce que cela m'a coûté (temps, énergie, plaisir, relations)", type: "textarea" },
      { id: "excellence_standard", label: "Version excellenciste du standard (élevé mais atteignable, tolérant à l'écart)", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 10,
  },
  "perf_good_enough": {
    titleKey: "perf.goodEnough.title",
    defaultTitle: "Expérience « suffisamment bon »",
    descKey: "perf.goodEnough.desc",
    defaultDesc: "Une exposition à l'imperfection : choisissez une tâche, fixez à l'avance un critère « suffisamment bon » (environ 80 % de votre idéal), prédisez ce qui va se passer, réalisez la tâche en vous arrêtant au critère, puis comparez la prédiction et la réalité.",
    fields: [
      { id: "task", label: "La tâche choisie", type: "text" },
      { id: "perfect_version", label: "À quoi ressemblerait la version « parfaite » (et combien de temps elle prendrait)", type: "textarea" },
      { id: "good_enough_criterion", label: "Mon critère « suffisamment bon » (80 %), défini AVANT de commencer", type: "textarea" },
      { id: "prediction", label: "Ma prédiction : ce qui va se passer si je m'arrête au critère", type: "textarea" },
      { id: "predicted_anxiety", label: "Anxiété prévue (0-10)", type: "number", min: 0, max: 10 },
      { id: "done", label: "Réalisé en m'arrêtant au critère ?", type: "select", options: ["Oui, je me suis arrêté(e) au critère", "En partie (j'ai un peu dépassé)", "Non, j'ai continué jusqu'au « parfait »", "Pas encore fait"] },
      { id: "reality", label: "Ce qui s'est réellement passé (réactions des autres, conséquences concrètes)", type: "textarea" },
      { id: "actual_anxiety", label: "Anxiété réelle après (0-10)", type: "number", min: 0, max: 10 },
      { id: "learning", label: "Ce que j'en apprends sur la prédiction et sur l'imperfection", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 10,
  },
  "perf_cost_benefit": {
    titleKey: "perf.costBenefit.title",
    defaultTitle: "Analyse coûts-bénéfices du perfectionnisme",
    descKey: "perf.costBenefit.desc",
    defaultDesc: "Un bilan honnête en quatre cases : ce que votre perfectionnisme vous apporte et vous coûte, aujourd'hui et à long terme. Nommer les bénéfices réels est indispensable pour comprendre pourquoi il persiste.",
    fields: [
      { id: "domain", label: "Domaine ou situation analysé(e)", type: "text" },
      { id: "benefits_short", label: "Bénéfices à court terme (ce qu'il m'apporte tout de suite : contrôle, sécurité, reconnaissance…)", type: "textarea" },
      { id: "costs_short", label: "Coûts à court terme (temps, stress, sommeil, plaisir perdu…)", type: "textarea" },
      { id: "benefits_long", label: "Bénéfices à long terme", type: "textarea" },
      { id: "costs_long", label: "Coûts à long terme (santé, relations, créativité, opportunités manquées…)", type: "textarea" },
      { id: "balance", label: "Où penche la balance ? (0 = tout pour le garder, 10 = tout pour l'assouplir)", type: "number", min: 0, max: 10 },
      { id: "conclusion", label: "Ma conclusion personnelle et un premier changement possible", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 5,
  },
  "perf_standards_continuum": {
    titleKey: "perf.continuum.title",
    defaultTitle: "Continuum des standards",
    descKey: "perf.continuum.desc",
    defaultDesc: "Pour un domaine, placez votre exigence actuelle sur une échelle de 0 à 100, puis délimitez la zone d'excellence (élevée mais atteignable) et la zone de perfection (inatteignable et coûteuse). Décidez où vous voulez vous situer.",
    fields: [
      { id: "domain", label: "Domaine", type: "select", options: ["Travail / études", "Relations / famille", "Apparence / corps", "Maison / organisation", "Loisirs / sport", "Parentalité", "Autre"] },
      { id: "current_standard", label: "Mon exigence actuelle (0-100)", type: "number", min: 0, max: 100 },
      { id: "current_description", label: "Concrètement, à quoi ressemble ce niveau d'exigence", type: "textarea" },
      { id: "excellence_zone", label: "Zone d'excellence : de … à … (ex. 75-90) et ce qu'elle signifie concrètement", type: "textarea" },
      { id: "perfection_zone", label: "Zone de perfection : à partir de … et ce qu'elle me coûte", type: "textarea" },
      { id: "others_standard", label: "Où je situerais une personne que j'admire et que je trouve équilibrée (0-100)", type: "number", min: 0, max: 100 },
      { id: "target_standard", label: "Où je choisis de me situer désormais (0-100)", type: "number", min: 0, max: 100 },
      { id: "first_step", label: "Un premier pas concret pour y aller cette semaine", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 8,
  },
  "barkley_identity_card": {
    titleKey: "barkley.identityCard.title",
    defaultTitle: "Carte d'identité TDAH de mon enfant",
    descKey: "barkley.identityCard.desc",
    defaultDesc: "Listez les situations difficiles fréquentes et identifiez les fonctions exécutives en jeu.",
    fields: [
      { id: "situation", label: "Situation (ex: devoirs, repas, coucher)", type: "text" },
      { id: "behavior", label: "Ce que je vois (comportement concret)", type: "textarea" },
      { id: "functions", label: "Fonctions en jeu", type: "select", options: ["Attention", "Inhibition", "Mémoire de travail", "Flexibilité", "Planification"] },
      { id: "feeling", label: "Ce que je ressens", type: "select", options: ["Fatigue", "Colère", "Impuissance", "Frustration", "Culpabilité", "Autre"] },
    ],
    repeatable: true,
    maxEntries: 5,
  },
  "barkley_special_moment": {
    titleKey: "barkley.specialMoment.title",
    defaultTitle: "Moment Spécial — Suivi de la semaine",
    descKey: "barkley.specialMoment.desc",
    defaultDesc: "Notez chaque jour si le moment spécial a eu lieu, l'activité, et le niveau d'agrément.",
    fields: [
      { id: "day", label: "Jour", type: "select", options: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"] },
      { id: "done", label: "Moment spécial réalisé ?", type: "select", options: ["Oui", "Non"] },
      { id: "activity", label: "Activité réalisée", type: "text" },
      { id: "parent_rating", label: "Agrément pour moi (0-10)", type: "number", min: 0, max: 10 },
      { id: "child_rating", label: "Agrément pour l'enfant (0-10)", type: "number", min: 0, max: 10 },
    ],
    repeatable: true,
    maxEntries: 7,
  },
  "barkley_effective_instructions": {
    titleKey: "barkley.instructions.title",
    defaultTitle: "Transformer mes consignes",
    descKey: "barkley.instructions.desc",
    defaultDesc: "Notez vos consignes habituelles et retravaillez-les pour les rendre courtes, positives et spécifiques.",
    fields: [
      { id: "original", label: "Ce que je dis habituellement", type: "textarea" },
      { id: "transformed", label: "Version transformée (courte, positive, spécifique)", type: "textarea" },
      { id: "tested", label: "Testé cette semaine ?", type: "select", options: ["Pas encore", "Oui - ça a marché", "Oui - à améliorer"] },
    ],
    repeatable: true,
    maxEntries: 5,
  },
  "barkley_token_system": {
    titleKey: "barkley.tokenSystem.title",
    defaultTitle: "Système de Points / Jetons",
    descKey: "barkley.tokenSystem.desc",
    defaultDesc: "Définissez les comportements ciblés, les récompenses et suivez les progrès.",
    fields: [
      { id: "behavior", label: "Comportement ciblé (formulé positivement)", type: "text" },
      { id: "points_per_day", label: "Points gagnés aujourd'hui", type: "number", min: 0, max: 10 },
      { id: "small_rewards", label: "Petites récompenses prévues", type: "text" },
      { id: "big_reward", label: "Grande récompense de la semaine", type: "text" },
      { id: "notes", label: "Observations", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 3,
  },
  "barkley_crisis_plan": {
    titleKey: "barkley.crisisPlan.title",
    defaultTitle: "Plan de Gestion de Crise",
    descKey: "barkley.crisisPlan.desc",
    defaultDesc: "Construisez votre protocole personnalisé pour gérer les crises.",
    fields: [
      { id: "warning_signs", label: "Signaux d'alerte (hausse du ton, cris, insultes...)", type: "textarea" },
      { id: "level1", label: "Niveau 1 — Ce que je ferai (rappel calme + alternative)", type: "textarea" },
      { id: "level2", label: "Niveau 2 — Annonce du time-out", type: "textarea" },
      { id: "level3", label: "Niveau 3 — Mise en time-out + silence", type: "textarea" },
      { id: "stop_doing", label: "Ce que je ne ferai PLUS (crier, menacer, insulter...)", type: "textarea" },
    ],
    repeatable: false,
  },
  "barkley_abc_analysis": {
    titleKey: "barkley.abcAnalysis.title",
    defaultTitle: "Analyse ABC Barkley",
    descKey: "barkley.abcAnalysis.desc",
    defaultDesc: "Analysez les antécédents, comportements et conséquences d'une situation problème.",
    fields: [
      { id: "antecedent", label: "A — Antécédents (ce qui se passe juste avant)", type: "textarea" },
      { id: "behavior", label: "B — Comportement (ce que l'enfant fait précisément)", type: "textarea" },
      { id: "consequence", label: "C — Conséquences (ce que font les adultes / ce que l'enfant obtient)", type: "textarea" },
      { id: "modifiable", label: "Antécédents modifiables", type: "textarea" },
      { id: "action_plan", label: "Mini plan d'action", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 3,
  },

  // ====== ENFANTS (6-12 ans) ======
  "barkley_child_volcano": {
    titleKey: "barkley.childVolcano.title",
    defaultTitle: "Mon Volcan Intérieur",
    descKey: "barkley.childVolcano.desc",
    defaultDesc: "Identifie les signaux de colère dans ton corps et choisis une stratégie pour te calmer.",
    fields: [
      { id: "situation", label: "Qu'est-ce qui s'est passé ?", type: "textarea" },
      { id: "body_signals", label: "Signaux dans mon corps", type: "select", options: ["Mains serrées", "Cœur qui bat vite", "Joues rouges", "Envie de crier", "Ventre noué", "Mâchoires serrées", "Autre"] },
      { id: "volcano_level", label: "Niveau de mon volcan (1-3)", type: "select", options: ["1 — Ça gronde un peu", "2 — Ça monte fort", "3 — Éruption !"] },
      { id: "strategy", label: "Stratégie utilisée pour me calmer", type: "select", options: ["Respirer profondément", "Compter jusqu'à 10", "M'éloigner", "Serrer un objet doux", "Boire de l'eau", "Dessiner ma colère", "En parler à un adulte"] },
      { id: "did_it_work", label: "Est-ce que ça a marché ?", type: "select", options: ["Oui, beaucoup", "Un peu", "Pas vraiment"] },
    ],
    repeatable: true,
    maxEntries: 5,
  },
  "barkley_child_autonomy_timer": {
    titleKey: "barkley.childAutonomy.title",
    defaultTitle: "Mon Timer d'Autonomie",
    descKey: "barkley.childAutonomy.desc",
    defaultDesc: "Entraîne-toi à rester seul(e) sur une activité. Note combien de temps tu as tenu !",
    fields: [
      { id: "day", label: "Jour", type: "select", options: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"] },
      { id: "activity", label: "Activité choisie", type: "text" },
      { id: "duration", label: "Durée tenue (en minutes)", type: "number", min: 0, max: 120 },
      { id: "pride", label: "Fierté (0-10)", type: "number", min: 0, max: 10 },
      { id: "disturbed", label: "J'ai dérangé l'adulte ?", type: "select", options: ["Non, pas du tout !", "Une seule fois", "Plusieurs fois"] },
    ],
    repeatable: true,
    maxEntries: 7,
  },
  "barkley_child_superpowers": {
    titleKey: "barkley.childSuperpowers.title",
    defaultTitle: "Mes Super Pouvoirs",
    descKey: "barkley.childSuperpowers.desc",
    defaultDesc: "Découvre tes forces et qualités ! Note une situation où tu as utilisé ton super pouvoir cette semaine.",
    fields: [
      { id: "superpower", label: "Mon super pouvoir", type: "select", options: ["Gentillesse", "Humour", "Créativité", "Courage", "Patience", "Générosité", "Curiosité", "Sportivité", "Sens de l'amitié", "Autre"] },
      { id: "situation", label: "Situation où je l'ai utilisé cette semaine", type: "textarea" },
      { id: "feeling", label: "Comment je me suis senti(e)", type: "select", options: ["Fier/Fière", "Content(e)", "Fort(e)", "Heureux/Heureuse", "Surpris(e)"] },
    ],
    repeatable: true,
    maxEntries: 5,
  },
  "barkley_child_solution_wheel": {
    titleKey: "barkley.childSolutions.title",
    defaultTitle: "La Roue des Solutions",
    descKey: "barkley.childSolutions.desc",
    defaultDesc: "Face à un problème, choisis une stratégie et évalue si ça a marché !",
    fields: [
      { id: "problem", label: "Le problème rencontré", type: "textarea" },
      { id: "emotion", label: "Émotion ressentie", type: "select", options: ["Colère", "Tristesse", "Frustration", "Peur", "Ennui", "Jalousie"] },
      { id: "strategy", label: "Stratégie choisie", type: "select", options: ["Demander de l'aide", "Respirer profondément", "M'éloigner un moment", "Proposer un compromis", "Attendre mon tour", "En parler calmement", "Dessiner / écrire ce que je ressens", "Trouver une autre activité"] },
      { id: "result", label: "Résultat", type: "select", options: ["Super, problème résolu !", "Ça a aidé un peu", "Pas résolu, je dois essayer autre chose"] },
    ],
    repeatable: true,
    maxEntries: 5,
  },
  "barkley_child_success_board": {
    titleKey: "barkley.childSuccess.title",
    defaultTitle: "Mon Tableau de Réussites",
    descKey: "barkley.childSuccess.desc",
    defaultDesc: "Chaque jour, note 1 à 3 choses que tu as bien faites, même toutes petites !",
    fields: [
      { id: "day", label: "Jour", type: "select", options: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"] },
      { id: "success1", label: "Réussite n°1", type: "text" },
      { id: "success2", label: "Réussite n°2 (optionnel)", type: "text" },
      { id: "success3", label: "Réussite n°3 (optionnel)", type: "text" },
      { id: "feeling", label: "Comment je me sens ce soir ?", type: "select", options: ["Très bien", "Bien", "Moyen", "Pas top"] },
    ],
    repeatable: true,
    maxEntries: 7,
  },
  "barkley_child_instruction_detective": {
    titleKey: "barkley.childDetective.title",
    defaultTitle: "Le Détective des Consignes",
    descKey: "barkley.childDetective.desc",
    defaultDesc: "Entraîne-toi à écouter une consigne et à la redire dans tes mots !",
    fields: [
      { id: "instruction_given", label: "La consigne qu'on m'a donnée", type: "text" },
      { id: "my_words", label: "Ce que j'ai compris (dans mes mots)", type: "textarea" },
      { id: "did_it", label: "J'ai réussi à le faire ?", type: "select", options: ["Oui, du premier coup !", "Oui, après un rappel", "J'ai eu besoin d'aide", "Pas cette fois"] },
      { id: "difficulty", label: "C'était facile ou difficile ?", type: "select", options: ["Très facile", "Assez facile", "Un peu difficile", "Très difficile"] },
    ],
    repeatable: true,
    maxEntries: 5,
  },

  // ====== ADOLESCENTS (13-17 ans) ======
  "barkley_teen_situation_analysis": {
    titleKey: "barkley.teenSituation.title",
    defaultTitle: "Mon Analyse de Situation",
    descKey: "barkley.teenSituation.desc",
    defaultDesc: "Analyse une situation difficile en identifiant tes pensées automatiques, émotions et alternatives.",
    fields: [
      { id: "situation", label: "Situation (qu'est-ce qui s'est passé ?)", type: "textarea" },
      { id: "automatic_thought", label: "Pensée automatique (qu'est-ce que je me suis dit ?)", type: "textarea" },
      { id: "emotion", label: "Émotion ressentie", type: "select", options: ["Colère", "Tristesse", "Anxiété", "Frustration", "Honte", "Culpabilité", "Dégoût", "Peur", "Autre"] },
      { id: "intensity", label: "Intensité (0-10)", type: "number", min: 0, max: 10 },
      { id: "alternative_thought", label: "Pensée alternative (comment voir les choses autrement ?)", type: "textarea" },
      { id: "action", label: "Ce que j'aurais pu faire différemment", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 3,
  },
  "barkley_teen_weekly_planner": {
    titleKey: "barkley.teenPlanner.title",
    defaultTitle: "Mon Planning de la Semaine",
    descKey: "barkley.teenPlanner.desc",
    defaultDesc: "Construis ton planning et évalue en fin de semaine ce que tu as tenu.",
    fields: [
      { id: "day", label: "Jour", type: "select", options: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"] },
      { id: "morning", label: "Matin — Tâches prévues", type: "textarea" },
      { id: "afternoon", label: "Après-midi — Tâches prévues", type: "textarea" },
      { id: "evening", label: "Soirée — Tâches / temps libre", type: "textarea" },
      { id: "completed", label: "Tenu ?", type: "select", options: ["Tout fait", "Presque tout", "La moitié", "Pas beaucoup"] },
    ],
    repeatable: true,
    maxEntries: 7,
  },
  "barkley_teen_emotion_journal": {
    titleKey: "barkley.teenEmotionJournal.title",
    defaultTitle: "Journal des Émotions Express",
    descKey: "barkley.teenEmotionJournal.desc",
    defaultDesc: "2 minutes par jour : note ton émotion, son intensité, et ce qui l'a déclenchée.",
    fields: [
      { id: "day", label: "Jour", type: "select", options: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"] },
      { id: "emotion", label: "Émotion principale du jour", type: "select", options: ["Joie", "Tristesse", "Colère", "Anxiété", "Calme", "Frustration", "Fierté", "Ennui", "Peur", "Espoir"] },
      { id: "intensity", label: "Intensité (0-10)", type: "number", min: 0, max: 10 },
      { id: "trigger", label: "Déclencheur (en une phrase)", type: "text" },
      { id: "reaction", label: "Ce que j'ai fait", type: "text" },
    ],
    repeatable: true,
    maxEntries: 7,
  },
  "barkley_teen_smart_goals": {
    titleKey: "barkley.teenSmartGoals.title",
    defaultTitle: "Mes Objectifs SMART",
    descKey: "barkley.teenSmartGoals.desc",
    defaultDesc: "Définis 1 à 3 objectifs concrets pour la semaine et évalue-toi en fin de semaine.",
    fields: [
      { id: "goal", label: "Objectif (Spécifique, Mesurable, Atteignable)", type: "textarea" },
      { id: "deadline", label: "Échéance (quand ?)", type: "text" },
      { id: "steps", label: "Étapes pour y arriver", type: "textarea" },
      { id: "result", label: "Auto-évaluation en fin de semaine", type: "select", options: ["Atteint !", "Presque atteint", "En cours", "Pas atteint — je réessaie"] },
      { id: "lesson", label: "Ce que j'ai appris", type: "textarea" },
    ],
    repeatable: true,
    maxEntries: 3,
  },
  "barkley_teen_cnv": {
    titleKey: "barkley.teenCNV.title",
    defaultTitle: "Communication Non-Violente (CNV)",
    descKey: "barkley.teenCNV.desc",
    defaultDesc: "Entraîne-toi à formuler des demandes en 4 étapes : Observation → Sentiment → Besoin → Demande.",
    fields: [
      { id: "context", label: "Situation (avec qui, quand, où ?)", type: "text" },
      { id: "observation", label: "1. Observation (les faits, sans jugement)", type: "textarea" },
      { id: "feeling", label: "2. Sentiment (ce que je ressens)", type: "textarea" },
      { id: "need", label: "3. Besoin (ce dont j'ai besoin)", type: "textarea" },
      { id: "request", label: "4. Demande (ce que je demande concrètement)", type: "textarea" },
      { id: "tried", label: "Essayé en vrai ?", type: "select", options: ["Pas encore", "Oui — bien reçu", "Oui — à améliorer"] },
    ],
    repeatable: true,
    maxEntries: 3,
  },
  "barkley_teen_weekly_review": {
    titleKey: "barkley.teenWeeklyReview.title",
    defaultTitle: "Mon Bilan Hebdo",
    descKey: "barkley.teenWeeklyReview.desc",
    defaultDesc: "Un bilan rapide de ta semaine : réussites, difficultés, et objectif pour la suite.",
    fields: [
      { id: "went_well", label: "Ce qui a bien marché cette semaine", type: "textarea" },
      { id: "was_hard", label: "Ce qui a été difficile", type: "textarea" },
      { id: "proud_of", label: "Une chose dont je suis fier/fière", type: "textarea" },
      { id: "next_goal", label: "Mon objectif pour la semaine prochaine", type: "textarea" },
      { id: "mood", label: "Mon humeur globale cette semaine", type: "select", options: ["Très bien", "Bien", "Moyen", "Pas top", "Difficile"] },
    ],
    repeatable: false,
  },

  // =============== ACT Exercises (Russ Harris) ===============

  "act_dropping_anchor": {
    titleKey: "act.droppingAnchor.title",
    defaultTitle: "Jeter l'Ancre",
    descKey: "act.droppingAnchor.desc",
    defaultDesc: "Technique d'ancrage ACT en 3 étapes (ACE) : Accepter, Connecter, S'Engager. Permet de se stabiliser lors de tempêtes émotionnelles.",
    fields: [
      { id: "trigger", label: "Situation ou émotion déclencheuse", type: "textarea", placeholder: "Qu'est-ce qui a provoqué la tempête émotionnelle ?" },
      { id: "acknowledge", label: "A — Accepter : Nommez ce qui est présent (pensées, émotions, sensations)", type: "textarea", placeholder: "Ex: 'Je remarque de l'anxiété dans ma poitrine, des pensées de doute...'" },
      { id: "connect", label: "C — Connecter : Décrivez comment vous vous êtes reconnecté à votre corps et au moment présent", type: "textarea", placeholder: "Ex: 'J'ai pressé mes pieds sur le sol, étiré mon dos, regardé autour de moi...'" },
      { id: "engage", label: "E — S'Engager : Quelle action alignée sur vos valeurs avez-vous choisie ?", type: "textarea", placeholder: "Ex: 'J'ai décidé de reprendre ma tâche calmement...'" },
      { id: "effectiveness", label: "Efficacité perçue (1-10)", type: "select", options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"] },
    ],
    repeatable: false,
  },

  "act_expansion": {
    titleKey: "act.expansion.title",
    defaultTitle: "Faire de la Place (Expansion)",
    descKey: "act.expansion.desc",
    defaultDesc: "Apprenez à accueillir les émotions difficiles au lieu de lutter contre elles. Basé sur la technique d'expansion de Russ Harris.",
    fields: [
      { id: "emotion", label: "Émotion difficile à accueillir", type: "text", placeholder: "Ex: anxiété, tristesse, colère..." },
      { id: "body_location", label: "Où ressentez-vous cette émotion dans votre corps ?", type: "textarea", placeholder: "Ex: une boule dans la gorge, une tension dans le ventre..." },
      { id: "observe", label: "Observez : Décrivez la sensation (forme, taille, température, mouvement)", type: "textarea", placeholder: "Observez comme un scientifique curieux... Quelle est sa texture ? Sa couleur si elle en avait une ?" },
      { id: "breathe_into", label: "Respirez dans cette zone : Qu'avez-vous observé en respirant autour de la sensation ?", type: "textarea", placeholder: "Ex: 'En respirant vers cette zone, la sensation s'est légèrement desserrée...'" },
      { id: "make_room", label: "Faites de la place : Qu'avez-vous dit à cette émotion pour l'accueillir ?", type: "textarea", placeholder: "Ex: 'Je te fais de la place. Tu es la bienvenue ici. Je ne lutte plus contre toi.'" },
      { id: "after_feeling", label: "Comment vous sentez-vous après cet exercice ?", type: "textarea" },
    ],
    repeatable: false,
  },

  "act_observer_self": {
    titleKey: "act.observerSelf.title",
    defaultTitle: "Le Soi Observateur",
    descKey: "act.observerSelf.desc",
    defaultDesc: "Connectez-vous à la partie de vous qui observe sans juger — le 'soi comme contexte'. Vous n'êtes pas vos pensées ni vos émotions.",
    fields: [
      { id: "current_thoughts", label: "Quelles pensées traversent votre esprit en ce moment ?", type: "textarea", placeholder: "Listez-les sans les juger..." },
      { id: "current_emotions", label: "Quelles émotions sont présentes ?", type: "textarea", placeholder: "Nommez-les simplement..." },
      { id: "notice_observer", label: "Notez : Qui est celui/celle qui observe ces pensées et émotions ?", type: "textarea", placeholder: "Remarquez qu'il y a une partie de vous qui peut observer tout cela — un 'vous' qui est toujours là, comme le ciel derrière les nuages." },
      { id: "sky_metaphor", label: "Métaphore du ciel : Vos pensées et émotions sont comme des nuages. Décrivez votre 'ciel'", type: "textarea", placeholder: "Comment est votre ciel intérieur aujourd'hui ? Quels nuages passent ? Le ciel (vous) reste toujours intact." },
      { id: "insight", label: "Qu'avez-vous appris ou ressenti grâce à cet exercice ?", type: "textarea" },
    ],
    repeatable: false,
  },

  "act_choice_point": {
    titleKey: "act.choicePoint.title",
    defaultTitle: "Le Point de Choix",
    descKey: "act.choicePoint.desc",
    defaultDesc: "Outil central de l'ACT : identifiez ce qui vous piège (pensées, émotions) et choisissez des actions qui vous rapprochent de la personne que vous voulez être.",
    fields: [
      { id: "situation", label: "Situation difficile / déclencheur", type: "textarea", placeholder: "Décrivez la situation qui a créé un 'point de choix'..." },
      { id: "hooks", label: "Ce qui vous 'crochète' (pensées, émotions, souvenirs, sensations)", type: "textarea", placeholder: "Ex: 'La pensée que je ne suis pas capable', 'une boule d'anxiété'..." },
      { id: "away_moves", label: "Mouvements d'ÉLOIGNEMENT : Que faites-vous habituellement pour éviter ? (fuite, lutte, figement)", type: "textarea", placeholder: "Ex: procrastiner, m'isoler, critiquer les autres, me critiquer..." },
      { id: "values_at_stake", label: "Valeurs en jeu dans cette situation", type: "textarea", placeholder: "Ex: être un parent présent, être courageux, prendre soin de ma santé..." },
      { id: "toward_moves", label: "Mouvements de RAPPROCHEMENT : Quelle action concrète pouvez-vous faire vers vos valeurs, même petite ?", type: "textarea", placeholder: "Ex: passer 10 minutes avec mon enfant, envoyer ce message difficile..." },
      { id: "chosen_action", label: "Action choisie", type: "textarea", placeholder: "Que décidez-vous de faire maintenant ?" },
    ],
    repeatable: false,
  },

  "act_matrix": {
    titleKey: "act.matrix.title",
    defaultTitle: "La Matrice ACT",
    descKey: "act.matrix.desc",
    defaultDesc: "La Matrice ACT divise votre expérience en 4 quadrants : ce qui compte (valeurs), ce qui bloque (obstacles internes), ce que vous faites pour fuir, et ce que vous faites pour avancer.",
    fields: [
      { id: "important", label: "Quadrant 1 — Ce qui COMPTE : Qui/quoi est important pour vous ?", type: "textarea", placeholder: "Famille, santé, créativité, authenticité..." },
      { id: "inner_obstacles", label: "Quadrant 2 — Ce qui BLOQUE (expériences internes) : Pensées, émotions, souvenirs qui apparaissent quand vous voulez avancer", type: "textarea", placeholder: "Peur du rejet, pensée 'je ne mérite pas', fatigue chronique..." },
      { id: "away_behaviors", label: "Quadrant 3 — Ce que je fais pour FUIR (comportements d'éloignement)", type: "textarea", placeholder: "Scroller sur le téléphone, éviter les conversations, remettre à demain..." },
      { id: "toward_behaviors", label: "Quadrant 4 — Ce que je fais pour AVANCER (comportements de rapprochement)", type: "textarea", placeholder: "Appeler un ami, faire du sport, exprimer mes besoins..." },
      { id: "awareness", label: "En regardant cette matrice, que remarquez-vous ?", type: "textarea" },
    ],
    repeatable: false,
  },

  "act_committed_action": {
    titleKey: "act.committedAction.title",
    defaultTitle: "Action Engagée",
    descKey: "act.committedAction.desc",
    defaultDesc: "Définissez des objectifs SMART alignés sur vos valeurs et suivez leur réalisation. L'ACT ne vise pas à se sentir mieux, mais à vivre mieux.",
    fields: [
      { id: "value", label: "Valeur que vous souhaitez honorer", type: "text", placeholder: "Ex: Être un parent attentif, cultiver ma santé..." },
      { id: "goal", label: "Objectif SMART aligné sur cette valeur", type: "textarea", placeholder: "Spécifique, Mesurable, Atteignable, Réaliste, Temporel. Ex: 'Cette semaine, je vais marcher 20 min chaque matin avant le travail'" },
      { id: "smallest_step", label: "Le plus petit pas possible pour commencer", type: "textarea", placeholder: "Quel est le premier micro-pas ? Ex: 'Poser mes chaussures de marche près de la porte ce soir'" },
      { id: "obstacles", label: "Obstacles internes anticipés (pensées, émotions qui vont apparaître)", type: "textarea", placeholder: "Ex: 'La pensée que c'est inutile', 'l'envie de rester au lit'" },
      { id: "willingness", label: "Êtes-vous prêt(e) à accueillir ces obstacles pour avancer vers cette valeur ?", type: "select", options: ["Oui, totalement", "Oui, avec difficulté", "Partiellement", "Pas encore"] },
      { id: "done", label: "Avez-vous réalisé l'action ?", type: "select", options: ["Pas encore", "Oui, complètement", "Oui, partiellement", "Non, mais j'ai appris quelque chose"] },
      { id: "reflection", label: "Réflexion : Qu'avez-vous appris ?", type: "textarea" },
    ],
    repeatable: false,
  },

  "act_reality_witness": {
    titleKey: "act.realityWitness.title",
    defaultTitle: "Soyez témoin de la réalité",
    descKey: "act.realityWitness.desc",
    defaultDesc: "Quand des pensées anxieuses, une rumination ou des pensées intrusives vous envahissent, passez de la concentration à l'attention. Revenez à la réalité en utilisant vos 5 sens. Soyez témoin de ce qui se passe ici et maintenant — autour de vous et en vous.",
    fields: [
      { id: "trigger", label: "Pensée anxieuse, rumination ou pensée intrusive déclenchante", type: "textarea", placeholder: "Ex: « Et si jamais...», ressassement d'une scène, scénario catastrophe..." },
      { id: "see", label: "👁️ Ce que je VOIS autour de moi", type: "textarea", placeholder: "Couleurs, formes, objets, lumière, mouvements..." },
      { id: "hear", label: "👂 Ce que j'ENTENDS", type: "textarea", placeholder: "Sons proches et lointains, conversations, silence, bruit ambiant..." },
      { id: "smell", label: "👃 Les PARFUMS / ODEURS que je perçois", type: "textarea", placeholder: "Odeur de café, parfum, air frais, lessive..." },
      { id: "touch", label: "✋ Le CONTACT (pieds au sol, vêtements, température)", type: "textarea", placeholder: "Sensation des pieds qui s'appuient, vêtements sur ma peau, chaleur ou fraîcheur de l'air, texture du siège..." },
      { id: "taste", label: "👅 Le GOÛT présent dans ma bouche", type: "textarea", placeholder: "Eau, café, menthe, neutre... décrivez-le simplement." },
      { id: "inner_witness", label: "🪞 Témoin de ma propre réalité : ce que je fais, où je suis, ce que je porte", type: "textarea", placeholder: "Décrivez vos vêtements (couleurs, texture), votre posture, où vous êtes assis, ce que vous faites en ce moment précis..." },
      { id: "after", label: "Comment je me sens après cet exercice ?", type: "textarea", placeholder: "Différence avec l'état initial : intensité de la rumination, ancrage dans le présent..." },
    ],
    repeatable: true,
  },

  "act_choice_question": {
    titleKey: "act.choiceQuestion.title",
    defaultTitle: "La question du choix",
    descKey: "act.choiceQuestion.desc",
    defaultDesc: "À chaque instant, l'action que je fais m'éloigne ou me rapproche de la personne que je suis vraiment ou que je voudrais être ? Les actions qui éloignent viennent des injonctions (je dois, il faut, je suis obligé). Les actions qui rapprochent viennent du choix (je préfère, je choisis, je peux).",
    fields: [
      { id: "current_action", label: "L'action que je fais en ce moment", type: "textarea", placeholder: "Décrivez ce que vous êtes en train de faire (ou sur le point de faire)..." },
      { id: "obligations", label: "📤 Actions/comportements qui m'ÉLOIGNENT (injonctions : « je dois… », « il faut… », « je suis obligé… »)", type: "textarea", placeholder: "Listez ici ce que vous faites par contrainte interne ou externe..." },
      { id: "preferences", label: "📥 Actions/comportements qui me RAPPROCHENT (choix : « je préfère… », « je choisis… », « je peux… »)", type: "textarea", placeholder: "Listez ici ce qui correspond à vos préférences et choix conscients..." },
      { id: "main_question", label: "Ma réponse à la question du choix : cette action m'éloigne ou me rapproche ?", type: "select", options: ["Elle me rapproche", "Elle m'éloigne", "Un peu des deux", "Je ne sais pas encore"] },
      { id: "new_action", label: "Si elle m'éloigne : quelle action pourrait me rapprocher de la personne que je veux être ?", type: "textarea", placeholder: "Une action concrète, même petite, alignée sur qui je veux être..." },
      { id: "commitment", label: "Je m'engage à...", type: "textarea", placeholder: "Formulez votre engagement avec un verbe de choix : « je choisis de... », « je préfère... », « je peux... »" },
    ],
    repeatable: true,
  },

  "act_control_myth": {
    titleKey: "act.controlMyth.title",
    defaultTitle: "Le mythe du contrôle",
    descKey: "act.controlMyth.desc",
    defaultDesc: "Quand les choses tournent mal, on perd souvent le sentiment de contrôle. Cet exercice vous aide à distinguer ce que vous NE contrôlez PAS de ce que vous pouvez contrôler partiellement — pour retrouver un sentiment de prise.",
    fields: [
      { id: "situation", label: "Situation difficile en ce moment", type: "textarea", placeholder: "Décrivez la situation qui vous donne le sentiment de perdre le contrôle..." },
      { id: "not_controllable", label: "❌ Ce que je NE contrôle PAS", type: "textarea", placeholder: "Les autres et ce qu'ils représentent, les aléas de la vie, mes émotions qui surviennent, le passé, l'avenir incertain, les réactions d'autrui..." },
      { id: "partially_controllable", label: "✅ Ce que je peux contrôler PARTIELLEMENT", type: "textarea", placeholder: "Mes actions, mes choix, mon discours intérieur et extérieur, l'utilisation de mon temps, la régulation de mes émotions, mes comportements, mes attentions..." },
      { id: "insight", label: "Que remarquez-vous en faisant cette différence ?", type: "textarea", placeholder: "Soulagement ? Frustration ? Lucidité ? Énergie disponible ?" },
      { id: "action", label: "Action concrète à poser dans mon champ de contrôle", type: "textarea", placeholder: "Une seule action, petite et possible, dans ce que vous pouvez maîtriser dès aujourd'hui..." },
    ],
    repeatable: true,
  },

  // =============== TDAH Adulte Exercises (Safren / TCCMontréal) ===============

  "tdah_priorities_abc": {
    titleKey: "tdah.priorities.title",
    defaultTitle: "Priorités ABC",
    descKey: "tdah.priorities.desc",
    defaultDesc: "Classez vos tâches selon le système ABC de Safren : A = urgent et important, B = important mais pas urgent, C = secondaire. Commencez toujours par les tâches A.",
    fields: [
      { id: "task_a1", label: "Tâche A (urgente + importante) #1", type: "text", placeholder: "Ex: Payer la facture d'électricité avant demain" },
      { id: "task_a2", label: "Tâche A #2 (optionnel)", type: "text", placeholder: "Ex: RDV médecin à confirmer" },
      { id: "task_b1", label: "Tâche B (importante, pas urgente) #1", type: "text", placeholder: "Ex: Trier mes emails cette semaine" },
      { id: "task_b2", label: "Tâche B #2 (optionnel)", type: "text", placeholder: "Ex: Préparer la réunion de lundi" },
      { id: "task_c1", label: "Tâche C (secondaire) #1", type: "text", placeholder: "Ex: Ranger le garage" },
      { id: "task_c2", label: "Tâche C #2 (optionnel)", type: "text", placeholder: "Ex: Acheter un cadeau d'anniversaire" },
      { id: "first_action", label: "Quelle tâche A allez-vous commencer maintenant ?", type: "textarea", placeholder: "Décrivez les premières micro-étapes..." },
      { id: "completed", label: "Bilan de fin de journée : qu'avez-vous accompli ?", type: "textarea" },
    ],
    repeatable: false,
  },

  "tdah_document_management": {
    titleKey: "tdah.documents.title",
    defaultTitle: "Gestion des Documents",
    descKey: "tdah.documents.desc",
    defaultDesc: "Mettez en place un système de gestion unique pour votre courrier, factures et documents importants. Le TDAH rend la gestion administrative particulièrement difficile — un système simple et constant est la clé.",
    fields: [
      { id: "current_system", label: "Décrivez votre système actuel (ou son absence)", type: "textarea", placeholder: "Comment gérez-vous actuellement votre courrier et vos factures ? Qu'est-ce qui ne fonctionne pas ?" },
      { id: "one_place", label: "Choisissez UN endroit unique pour déposer tous les documents entrants", type: "text", placeholder: "Ex: Une corbeille sur le bureau d'entrée" },
      { id: "categories", label: "Définissez 3-4 catégories simples pour le tri", type: "textarea", placeholder: "Ex: 1. À payer cette semaine 2. À traiter plus tard 3. À classer/archiver 4. À jeter" },
      { id: "routine", label: "Quand allez-vous trier ? (jour + heure fixe)", type: "text", placeholder: "Ex: Chaque dimanche à 10h pendant 20 minutes" },
      { id: "reminder", label: "Quel rappel allez-vous mettre en place ?", type: "text", placeholder: "Ex: Alarme téléphone chaque dimanche 9h55" },
      { id: "this_week", label: "Documents à traiter cette semaine", type: "textarea" },
    ],
    repeatable: false,
  },

  "tdah_attention_capacity": {
    titleKey: "tdah.attention.title",
    defaultTitle: "Capacité Attentionnelle",
    descKey: "tdah.attention.desc",
    defaultDesc: "Évaluez et entraînez votre capacité d'attention. Le TDAH affecte la durée d'attention soutenue — connaître vos limites permet de planifier des sessions de travail adaptées.",
    fields: [
      { id: "task_type", label: "Type de tâche testée", type: "text", placeholder: "Ex: Lecture, travail sur ordinateur, ménage..." },
      { id: "estimated_duration", label: "Combien de temps pensez-vous pouvoir vous concentrer sur cette tâche ?", type: "select", options: ["5 minutes", "10 minutes", "15 minutes", "20 minutes", "30 minutes", "45 minutes", "60 minutes+"] },
      { id: "actual_duration", label: "Combien de temps avez-vous réellement tenu avant la première distraction ?", type: "select", options: ["Moins de 5 min", "5-10 min", "10-15 min", "15-20 min", "20-30 min", "30-45 min", "45-60 min", "60 min+"] },
      { id: "distractions", label: "Qu'est-ce qui vous a distrait ?", type: "textarea", placeholder: "Ex: Mon téléphone, une pensée soudaine, un bruit, envie de faire autre chose..." },
      { id: "environment", label: "Décrivez votre environnement de travail", type: "textarea", placeholder: "Ex: Bureau à la maison, télé allumée à côté, téléphone sur le bureau..." },
      { id: "improvements", label: "Que pouvez-vous changer pour améliorer votre concentration ?", type: "textarea", placeholder: "Ex: Mettre le téléphone dans une autre pièce, utiliser un timer, casque anti-bruit..." },
      { id: "next_goal", label: "Objectif de durée pour la prochaine session", type: "text", placeholder: "Ex: Tenir 20 minutes sans interruption" },
    ],
    repeatable: false,
  },

  "tdah_automatic_thoughts": {
    titleKey: "tdah.thoughts.title",
    defaultTitle: "Pensées Automatiques TDAH",
    descKey: "tdah.thoughts.desc",
    defaultDesc: "Le TDAH génère des pensées automatiques spécifiques liées aux échecs répétés : 'Je suis paresseux', 'Je n'y arriverai jamais'. Identifiez et restructurez ces pensées avec le tableau de Safren.",
    fields: [
      { id: "situation", label: "Situation déclencheuse", type: "textarea", placeholder: "Ex: J'ai oublié un rendez-vous important..." },
      { id: "automatic_thought", label: "Pensée automatique", type: "textarea", placeholder: "Ex: 'Je suis incapable de gérer ma vie', 'Les autres n'oublient jamais rien'..." },
      { id: "emotion", label: "Émotion ressentie et intensité (0-10)", type: "text", placeholder: "Ex: Honte 8/10, frustration 7/10" },
      { id: "thinking_error", label: "Type d'erreur de pensée", type: "select", options: ["Tout ou rien (noir/blanc)", "Surgénéralisation (toujours/jamais)", "Disqualification du positif", "Lecture de pensée", "Catastrophisation", "Personnalisation", "Étiquetage (je suis nul)", "Raisonnement émotionnel", "Autre"] },
      { id: "evidence_for", label: "Preuves POUR cette pensée", type: "textarea", placeholder: "Quels faits soutiennent cette pensée ?" },
      { id: "evidence_against", label: "Preuves CONTRE cette pensée", type: "textarea", placeholder: "Quels faits contredisent cette pensée ? Tenez compte de votre TDAH comme facteur explicatif." },
      { id: "alternative_thought", label: "Pensée alternative réaliste", type: "textarea", placeholder: "Ex: 'J'ai oublié ce RDV parce que mon TDAH affecte ma mémoire de travail. Je peux mettre en place un système de rappels.'" },
      { id: "new_emotion", label: "Nouvelle émotion et intensité après restructuration", type: "text", placeholder: "Ex: Frustration 4/10, détermination 6/10" },
    ],
    repeatable: false,
  },

  "tdah_anti_procrastination": {
    titleKey: "tdah.procrastination.title",
    defaultTitle: "Anti-Procrastination",
    descKey: "tdah.procrastination.desc",
    defaultDesc: "La procrastination dans le TDAH est liée au déficit de dopamine et à la difficulté à initier des tâches non stimulantes. Décomposez chaque tâche en micro-étapes concrètes et commencez par la plus petite.",
    fields: [
      { id: "task", label: "Tâche que vous procrastinez", type: "text", placeholder: "Ex: Faire ma déclaration d'impôts" },
      { id: "why_avoiding", label: "Pourquoi l'évitez-vous ? (soyez honnête)", type: "textarea", placeholder: "Ex: C'est ennuyeux, je ne sais pas par où commencer, j'ai peur de faire des erreurs..." },
      { id: "step1", label: "Micro-étape 1 (5 min max)", type: "text", placeholder: "Ex: Rassembler les documents nécessaires" },
      { id: "step2", label: "Micro-étape 2 (5 min max)", type: "text", placeholder: "Ex: Ouvrir le site des impôts et me connecter" },
      { id: "step3", label: "Micro-étape 3 (5 min max)", type: "text", placeholder: "Ex: Remplir la première section (état civil)" },
      { id: "step4", label: "Micro-étape 4 (optionnel)", type: "text", placeholder: "Ex: Remplir la section revenus" },
      { id: "reward", label: "Récompense après avoir fait la première micro-étape", type: "text", placeholder: "Ex: 10 min de YouTube, un café, une marche..." },
      { id: "when", label: "Quand allez-vous commencer la micro-étape 1 ? (jour + heure exacte)", type: "text", placeholder: "Ex: Aujourd'hui à 14h30" },
      { id: "result", label: "Bilan : Qu'avez-vous accompli ?", type: "textarea" },
    ],
    repeatable: false,
  },

  "tdah_goals_motivation": {
    titleKey: "tdah.goals.title",
    defaultTitle: "Objectifs et Motivation",
    descKey: "tdah.goals.desc",
    defaultDesc: "Fixez des objectifs réalistes en tenant compte de votre TDAH. La clé : des objectifs petits, concrets, mesurables, avec des récompenses immédiates pour compenser le déficit de motivation intrinsèque.",
    fields: [
      { id: "domain", label: "Domaine de vie", type: "select", options: ["Travail/études", "Santé", "Relations", "Organisation", "Finances", "Loisirs", "Développement personnel"] },
      { id: "big_goal", label: "Objectif à long terme (ce que vous visez)", type: "textarea", placeholder: "Ex: Être plus organisé au travail" },
      { id: "weekly_goal", label: "Objectif de la semaine (petit, concret, mesurable)", type: "textarea", placeholder: "Ex: Utiliser mon agenda chaque matin pour noter 3 tâches" },
      { id: "daily_action", label: "Action quotidienne (1 habitude simple)", type: "text", placeholder: "Ex: Ouvrir mon agenda chaque matin à 8h30" },
      { id: "obstacle", label: "Principal obstacle anticipé", type: "textarea", placeholder: "Ex: J'oublie d'ouvrir l'agenda, je suis pressé le matin..." },
      { id: "strategy", label: "Stratégie pour surmonter cet obstacle", type: "textarea", placeholder: "Ex: Poser l'agenda sur mon clavier la veille au soir" },
      { id: "reward", label: "Récompense prévue si objectif atteint", type: "text", placeholder: "Ex: Me commander un bon repas vendredi soir" },
      { id: "weekly_review", label: "Bilan de fin de semaine : progression 1-10 et observations", type: "textarea" },
    ],
    repeatable: false,
  },
};

const BarkleyExercisePage = ({ user, logout }) => {
  const { exerciseType } = useParams();
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const config = EXERCISE_CONFIGS[exerciseType];
  const [entries, setEntries] = useState([{}]);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!config) return;
    if (!config.repeatable) {
      setEntries([{}]);
    }
    // Fetch past responses
    axios.get(`${API}/exercises/responses?exercise_type=${exerciseType}`, { withCredentials: true })
      .then(res => setHistory(res.data.slice(0, 5)))
      .catch(() => {});
  }, [exerciseType]);

  if (!config) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">Exercice non trouvé</p>
          <Link to="/patient"><Button variant="outline" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button></Link>
        </div>
      </DashboardLayout>
    );
  }

  const title = t(config.titleKey, config.defaultTitle);
  const description = t(config.descKey, config.defaultDesc);

  const updateEntry = (idx, fieldId, value) => {
    const updated = [...entries];
    updated[idx] = { ...updated[idx], [fieldId]: value };
    setEntries(updated);
  };

  const addEntry = () => {
    if (entries.length < (config.maxEntries || 10)) {
      setEntries([...entries, {}]);
    }
  };

  const removeEntry = (idx) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== idx));
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const saveRes = await axios.post(`${API}/exercises/respond`, {
        exercise_type: exerciseType,
        data: { entries },
      }, { withCredentials: true });
      toast.success(t("barkley.saved", "Exercice enregistré !"));
      if (user?.role === "patient") promptShare(saveRes.data, exerciseType);
      // Refresh history
      const res = await axios.get(`${API}/exercises/responses?exercise_type=${exerciseType}`, { withCredentials: true });
      setHistory(res.data.slice(0, 5));
      setEntries([{}]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field, entryIdx) => {
    const value = entries[entryIdx]?.[field.id] || "";
    if (field.type === "textarea") {
      return <Textarea value={value} onChange={(e) => updateEntry(entryIdx, field.id, e.target.value)} rows={3} className="mt-1" data-testid={`field-${field.id}-${entryIdx}`} />;
    }
    if (field.type === "select") {
      return (
        <Select value={value} onValueChange={(v) => updateEntry(entryIdx, field.id, v)}>
          <SelectTrigger className="mt-1" data-testid={`field-${field.id}-${entryIdx}`}><SelectValue placeholder="Choisir..." /></SelectTrigger>
          <SelectContent>{field.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (field.type === "number") {
      return <Input type="number" min={field.min} max={field.max} value={value} onChange={(e) => updateEntry(entryIdx, field.id, e.target.value)} className="mt-1 w-24" data-testid={`field-${field.id}-${entryIdx}`} />;
    }
    return <Input value={value} onChange={(e) => updateEntry(entryIdx, field.id, e.target.value)} className="mt-1" data-testid={`field-${field.id}-${entryIdx}`} />;
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/patient"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
          <div>
            <h1 className="font-heading text-2xl font-bold">{title}</h1>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>

        {/* Exercise Form */}
        {entries.map((entry, idx) => (
          <Card key={idx} data-testid={`entry-${idx}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">
                  {config.repeatable ? `${config.repeatable ? (exerciseType === "barkley_special_moment" ? config.fields[0].options[idx] || `Entrée ${idx + 1}` : `Situation ${idx + 1}`) : ""}` : title}
                </CardTitle>
                {config.repeatable && entries.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="text-destructive h-8">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.fields.map((field) => (
                <div key={field.id}>
                  <Label className="text-sm font-medium">{field.label}</Label>
                  {renderField(field, idx)}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {config.repeatable && entries.length < (config.maxEntries || 10) && (
          <Button variant="outline" onClick={addEntry} className="w-full gap-2" data-testid="add-entry-btn">
            <Plus className="w-4 h-4" /> Ajouter une entrée
          </Button>
        )}

        <Button onClick={handleSubmit} disabled={saving} className="w-full gap-2" data-testid="submit-barkley-btn">
          <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>

        {/* History */}
        {history.length > 0 && (
          <div className="space-y-3">
            <h2 className="font-heading text-lg font-semibold">{t("barkley.history", "Historique")}</h2>
            {history.map((resp, idx) => (
              <Card key={idx} className="bg-muted/30">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-2">
                    {new Date(resp.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  {resp.data?.entries?.map((entry, eIdx) => (
                    <div key={eIdx} className="mb-2 pb-2 border-b last:border-b-0 last:pb-0 last:mb-0">
                      {Object.entries(entry).map(([key, val]) => val && (
                        <p key={key} className="text-sm"><span className="font-medium">{key}:</span> {val}</p>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BarkleyExercisePage;
